/**
 * Referrals tab content for the Admin Dashboard.
 *
 * Renders the 5-tile analytics summary + Top Referrers table when the
 * `referralAnalytics` prop is populated. Returns null otherwise so the
 * parent doesn't have to do the conditional dance.
 *
 * Extracted from AdminDashboard.jsx for maintainability. State is owned
 * by the parent (fetched alongside the rest of the admin boot data) and
 * passed in as a prop.
 */
import React from 'react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Gift } from 'lucide-react';
import { OutletReferralsCard } from '@/components/admin/OutletReferralsCard';

export function ReferralsTabContent({ referralAnalytics }) {
  return (
    <div className="space-y-8">
      {/* Outlet / marketing referral tracker — admin-managed ?ref= codes
          for newsletter pitches, podcasts, partnership links. Always
          visible, independent of the user-to-user analytics below. */}
      <OutletReferralsCard />

      {/* User-to-user "Invite a Friend" analytics — only renders when
          the parent has fetched data. */}
      {referralAnalytics && (
        <>
          <h3 className="text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
            <Gift className="h-5 w-5 text-[#8B5CF6]" />
            Invite a Friend! Analytics
          </h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-[#8B5CF6]/30">
          <CardHeader className="pb-2">
            <CardDescription>Total Friends Invited</CardDescription>
            <CardTitle className="text-2xl text-[#8B5CF6]">
              {referralAnalytics.total_referrals}
            </CardTitle>
          </CardHeader>
        </Card>

        <Card className="border-green-500/30">
          <CardHeader className="pb-2">
            <CardDescription>Signups</CardDescription>
            <CardTitle className="text-2xl text-green-500">
              {referralAnalytics.successful_signups}
            </CardTitle>
          </CardHeader>
        </Card>

        <Card className="border-amber-500/30">
          <CardHeader className="pb-2">
            <CardDescription>VIP Conversions</CardDescription>
            <CardTitle className="text-2xl text-amber-500">
              {referralAnalytics.vip_conversions}
            </CardTitle>
          </CardHeader>
        </Card>

        <Card className="border-[#00E5CC]/30">
          <CardHeader className="pb-2">
            <CardDescription>Credits Earned</CardDescription>
            <CardTitle className="text-2xl text-[#00E5CC]">
              {referralAnalytics.total_credits_earned} mo
            </CardTitle>
          </CardHeader>
        </Card>

        <Card className="border-blue-500/30">
          <CardHeader className="pb-2">
            <CardDescription>Credits Used</CardDescription>
            <CardTitle className="text-2xl text-blue-500">
              {referralAnalytics.total_credits_used} mo
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Top Referrers Table */}
      {referralAnalytics.top_referrers?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Top Referrers</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-center">Total Friends Invited</TableHead>
                  <TableHead className="text-center">VIP Conversions</TableHead>
                  <TableHead className="text-center">Credits Earned</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referralAnalytics.top_referrers.map((referrer, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{referrer.email}</TableCell>
                    <TableCell className="text-center">{referrer.total_referrals}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant={referrer.vip_referrals > 0 ? 'default' : 'secondary'}>
                        {referrer.vip_referrals}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center text-[#00E5CC] font-medium">
                      {referrer.credits_earned} mo
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
        </>
      )}
    </div>
  );
}

export default ReferralsTabContent;
